import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { isEmpty, merge } from 'lodash';
import { LinkedServiceAdapter } from '../linkedservices/linkedservice.adapter';
import {
    ExecuteSSISPackageParameterConfig,
    InvokeSSISPackageActivity,
    LakehouseLinkedService,
    LinkedService,
    Owned,
    isLogicAppExpression,
    ReferenceAdapter,
    WorkspaceBuildInLinkedService,
    linkedServiceReferenceToResource
} from 'shell-models-lib';
import { isExpressionObject } from 'app/shared/utils/expression.util';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from '../linkedservices/azure-key-vault.util';
import { SSISPackageActivityAdapter } from './ssis-activity.adapter.base';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { LoggerService } from 'app/logger/logger.service';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { generateUUID } from 'app/shared/utils/uuid.util';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(InvokeSSISPackageActivity),
    asynchronous: true,
    disabled: !__IS_FABRIC_DPE__,
    adapts: {
        name: '*',
        type: '*',
        description: '*',
        dependsOn: '*',
        policy: '*',
        userProperties: '*',
        typeProperties: {
            packageLocation: '*',
            loggingLevel: '*',
            projectParameters: '*',
            packageParameters: '*',
            projectConnectionManagers: '*',
            packageConnectionManagers: '*',
            propertyOverrides: '*',
            logLocation: '*'
        }
    }
})
export class InvokeSSISPackageActivityAdapter extends SSISPackageActivityAdapter<
    InvokeSSISPackageActivity,
    Model.InvokeSSISPackageActivity
> {
    public model(): IModel {
        return Metamodel.modelFor(InvokeSSISPackageActivity);
    }

    protected async _toResource(activity: InvokeSSISPackageActivity): Promise<Model.InvokeSSISPackageActivity> {
        const modelActivity: Model.InvokeSSISPackageActivity = super._getCommonActivityProperties(
            activity,
            'ExecuteSSISPackage'
        ) as Model.InvokeSSISPackageActivity;

        modelActivity.typeProperties = {
            packageLocation: await this._getPkgLocationSettings(activity)
        };

        if (ValueAdapter.toResource(activity, InvokeSSISPackageActivity.ENABLE_LOGGING)) {
            modelActivity.typeProperties.loggingLevel = ValueAdapter.toResource(
                activity,
                InvokeSSISPackageActivity.LOGGING_LEVEL
            );
            modelActivity.typeProperties.logLocation = this._getLoggingSettings(activity);
            modelActivity.typeProperties.logLocation.typeProperties = {
                connectionSettings: modelActivity.typeProperties.packageLocation.typeProperties.connectionSettings
            };
        } else {
            modelActivity.typeProperties.loggingLevel = 'None';
        }

        modelActivity.typeProperties.propertyOverrides = this.toPropertyOverrideObject(activity.propertyOverrides);

        modelActivity.typeProperties.projectParameters = this.toActivityParameterObject(
            activity,
            ExecuteSSISPackageParameterConfig.SCOPE_PROJECT
        );
        modelActivity.typeProperties.packageParameters = this.toActivityParameterObject(
            activity,
            ExecuteSSISPackageParameterConfig.SCOPE_PACKAGE
        );

        modelActivity.typeProperties.projectConnectionManagers = this.toActivityConnectionManagerObject(
            activity,
            ExecuteSSISPackageParameterConfig.SCOPE_PROJECT
        );
        modelActivity.typeProperties.packageConnectionManagers = this.toActivityConnectionManagerObject(
            activity,
            ExecuteSSISPackageParameterConfig.SCOPE_PACKAGE
        );

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            merge(modelActivity, parsed);
        }
        return modelActivity;
    }

    protected async _toModel(modelActivity: Model.InvokeSSISPackageActivity): Promise<InvokeSSISPackageActivity> {
        const loggerService = await getServiceInstance<LoggerService>(this.context.contextData, 'LoggerService');
        const activity: InvokeSSISPackageActivity = InvokeSSISPackageActivity.MODEL.create<InvokeSSISPackageActivity>(
            modelActivity.name,
            modelActivity.description
        );
        this._updatedCommonProperties(modelActivity, activity);

        let linkedServiceModel: IModel;
        let linkedServiceName: Model.LinkedServiceReference = modelActivity.linkedServiceName;
        const pkgLocationConnSettingResource =
            modelActivity.typeProperties.packageLocation?.typeProperties?.connectionSettings;
        const linkedServiceType = pkgLocationConnSettingResource?.properties?.type;

            if (!isEmpty(pkgLocationConnSettingResource)) {

            // handle dmts credential
            if (linkedServiceType && pkgLocationConnSettingResource.properties.linkedServiceName) {
                const linkedServiceRegistryService = await getServiceInstance<LinkedServiceRegistryService>(
                    this.context.contextData,
                    'LinkedServiceRegistryService'
                );
                const linkedServiceMetadata =
                    linkedServiceRegistryService.getLinkedServiceConfiguration(linkedServiceType);
                if (linkedServiceMetadata?.dmtsCredentialConnectionType) {
                    if (isLogicAppExpression(pkgLocationConnSettingResource.properties.linkedServiceName.referenceName)) {
                        const model = Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType);
                        if (model) {
                            const templateLinkedService = Owned.create<LinkedService>(model, activity);
                            templateLinkedService.name = pkgLocationConnSettingResource.name || generateUUID().replaceAll('-', '_');
                            ValueAdapter.toModel(
                                templateLinkedService,
                                Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType).attribute(
                                    'objectId'
                                ),
                                pkgLocationConnSettingResource.properties.linkedServiceName.referenceName
                            );
                            templateLinkedService.parameterized = true;
                            activity.artifactDmtsCredentialForSsisStore = templateLinkedService;
                        }
                        loggerService.log('fabric-ssis-activity.adapter', 'ssis store credential linkedService is parameterized', {
                            linkedServiceType
                        });
                    } else {
                        activity.artifactDmtsCredentialForSsisStore = ReferenceAdapter.toProxyModel(
                            Metamodel.modelByName(linkedServiceMetadata.dmtsCredentialConnectionType),
                            pkgLocationConnSettingResource.properties.linkedServiceName,
                            this.context
                        );
                    }
                }
            }

            if (this._shouldGetArtifactDMTSConnectionFromSolutionTemplate()) {
                const dmtsCredentialKey = `${modelActivity.name}/ExecuteSSISPackage`;
                const dmtsCredentialForArtifact = this.context.adapterConfig.artifactDMTSCredentials.get(dmtsCredentialKey);
                if (dmtsCredentialForArtifact != null) {
                    activity.artifactDmtsCredentialForSsisStore = dmtsCredentialForArtifact;
                }
            }

            linkedServiceModel = Metamodel.modelByName(linkedServiceType);
            const linkedServiceAdapter = this.context.asyncAdapterFor(
                linkedServiceModel
            ) as LinkedServiceAdapter<LakehouseLinkedService>;
            // Handle workspace built-in linked service in dynamic content
            const lsModel: WorkspaceBuildInLinkedService = Owned.create<WorkspaceBuildInLinkedService>(
                linkedServiceModel,
                activity
            );
            await linkedServiceAdapter?.toModel(pkgLocationConnSettingResource, lsModel);
            if (lsModel?.parameterized || activity?.artifactDmtsCredentialForSsisStore?.parameterized) {
                activity.linkedService = lsModel;
                loggerService.log('fabric-ssis-activity.adapter', 'ssis store linkedService is parameterized', { linkedServiceType });
            } else {
                linkedServiceName = {
                    type: 'LinkedServiceReference',
                    referenceName: linkedServiceAdapter.getArtifactId(pkgLocationConnSettingResource),
                    workspaceObjectId: linkedServiceAdapter.getWorkspaceObjectId(pkgLocationConnSettingResource)
                };
            }
        }

        if (linkedServiceName != null) {
            activity.linkedService = ReferenceAdapter.toProxyModel<LinkedService>(
                linkedServiceType ? Metamodel.modelByName(linkedServiceType):   Metamodel.modelFor(LinkedService),
                linkedServiceName,
                this.context
            );
        }

        if (modelActivity.typeProperties) {
            if (modelActivity.typeProperties.packageLocation) {
                activity.fileType = !!modelActivity.typeProperties.packageLocation.projectPath
                    ? 'ProjectFile'
                    : 'PackageFile';
                if (activity.fileType === 'PackageFile') {
                    ValueAdapter.toModel(
                        activity,
                        InvokeSSISPackageActivity.PACKAGE_PATH,
                        modelActivity.typeProperties.packageLocation.packagePath
                    );
                    ValueAdapter.toModel(
                        activity,
                        InvokeSSISPackageActivity.CONFIGURATION_FILE,
                        modelActivity.typeProperties.packageLocation.typeProperties.configurationPath
                    );
                } else if (activity.fileType === 'ProjectFile') {
                    ValueAdapter.toModel(
                        activity,
                        InvokeSSISPackageActivity.PROJECT_PATH,
                        modelActivity.typeProperties.packageLocation.projectPath
                    );
                    ValueAdapter.toModel(
                        activity,
                        InvokeSSISPackageActivity.PACKAGE_NAME,
                        modelActivity.typeProperties.packageLocation.typeProperties.packageName
                    );
                }
                // Set package password
                const packagePassword = modelActivity.typeProperties.packageLocation.typeProperties.packagePassword;
                if (packagePassword) {
                    ValueAdapter.toModel(
                        activity,
                        InvokeSSISPackageActivity.PACKAGE_PASSWORD,
                        isExpressionObject(packagePassword)
                            ? packagePassword.value
                            : secretBaseToPasswordOrAKV(packagePassword, true, this.context)
                    );
                }
            }
            if (modelActivity.typeProperties.logLocation || modelActivity.typeProperties.loggingLevel !== 'None') {
                ValueAdapter.toModel(activity, InvokeSSISPackageActivity.ENABLE_LOGGING, true);
                ValueAdapter.toModel(
                    activity,
                    InvokeSSISPackageActivity.LOGGING_LEVEL,
                    modelActivity.typeProperties.loggingLevel
                );
                if (modelActivity.typeProperties.logLocation) {
                    activity.logOutputType = modelActivity.typeProperties.logLocation.type as
                        | 'Table'
                        | 'File'
                        | 'FileAndTable';
                    ValueAdapter.toModel(
                        activity,
                        InvokeSSISPackageActivity.LOG_PATH,
                        modelActivity.typeProperties.logLocation.logPath
                    );
                    ValueAdapter.toModel(
                        activity,
                        InvokeSSISPackageActivity.LOG_OUTPUT_TABLE_PREFIX,
                        modelActivity.typeProperties.logLocation.tableNamePrefix
                    );
                }
            }
        }

        // Set parameters
        const projectParameters = this.toSsisParameterClass(
            modelActivity.typeProperties.projectParameters,
            activity,
            ExecuteSSISPackageParameterConfig.SCOPE_PROJECT, this.context
        );
        const packageParameters = this.toSsisParameterClass(
            modelActivity.typeProperties.packageParameters,
            activity,
            ExecuteSSISPackageParameterConfig.SCOPE_PACKAGE, this.context
        );
        activity.parameters = projectParameters.concat(packageParameters);

        const projectConnectionManagers = this.toActivityConnectionManagerClass(
            modelActivity.typeProperties.projectConnectionManagers,
            activity,
            ExecuteSSISPackageParameterConfig.SCOPE_PROJECT, this.context
        );
        const packageConnectionManagers = this.toActivityConnectionManagerClass(
            modelActivity.typeProperties.packageConnectionManagers,
            activity,
            ExecuteSSISPackageParameterConfig.SCOPE_PACKAGE, this.context
        );
        activity.connectionManagerParameters = projectConnectionManagers.concat(packageConnectionManagers);

        activity.propertyOverrides = this.toPropertyOverrideClass(
            modelActivity.typeProperties.propertyOverrides,
            activity
        );
        return activity;
    }

    private async _getPkgLocationSettings(activity: InvokeSSISPackageActivity): Promise<Model.SSISPackageLocation> {
        const locTypeProperties: Model.SSISPackageLocationTypeProperties = {};
        const result: Model.SSISPackageLocation = {
            type: activity.packageLocation,
            typeProperties: locTypeProperties
        };
        const packagePassword = ValueAdapter.toResource(activity, InvokeSSISPackageActivity.PACKAGE_PASSWORD);
        locTypeProperties.packagePassword = packagePassword
            ? isExpressionObject(packagePassword)
                ? packagePassword
                : passwordOrAKVtoSecretBase(packagePassword, this.context)
            : undefined;
        if (activity.fileType === 'PackageFile') {
            result.packagePath = ValueAdapter.toResource(activity, InvokeSSISPackageActivity.PACKAGE_PATH);
            locTypeProperties.configurationPath = ValueAdapter.toResource(
                activity,
                InvokeSSISPackageActivity.CONFIGURATION_FILE
            );
        } else if (activity.fileType === 'ProjectFile') {
            result.projectPath = ValueAdapter.toResource(activity, InvokeSSISPackageActivity.PROJECT_PATH);
            locTypeProperties.packageName = ValueAdapter.toResource(activity, InvokeSSISPackageActivity.PACKAGE_NAME);
        }

        if (activity.linkedService) {
            if (activity.linkedService.isWorkspaceBuiltIn) {
                const linkedServiceAdapter = this.context.asyncAdapterFor(Metamodel.modelOf(activity.linkedService));
                const linkedService: Model.LinkedServiceResource = await linkedServiceAdapter.toResource(
                    activity.linkedService
                );
                if (activity.artifactDmtsCredentialForSsisStore) {
                    linkedService.properties.linkedServiceName = linkedServiceReferenceToResource(
                        activity.artifactDmtsCredentialForSsisStore,
                        this.context
                    );
                }
                Reflect.set(linkedService.properties.typeProperties, 'rootFolder', 'Files');
                locTypeProperties.connectionSettings = linkedService;
            }
            // current we only support Lakehouse for ssis store linkedService
        }
        return result;
    }

    private _getLoggingSettings(activity: InvokeSSISPackageActivity): Model.SSISLogLocation {
        return {
            type: activity.logOutputType,
            logPath:
                activity.logOutputType === 'Table'
                    ? undefined
                    : ValueAdapter.toResource(activity, InvokeSSISPackageActivity.LOG_PATH),
            tableNamePrefix:
                activity.logOutputType === 'File'
                    ? undefined
                    : ValueAdapter.toResource(activity, InvokeSSISPackageActivity.LOG_OUTPUT_TABLE_PREFIX)
        };
    }
}
